/**
 * NestCanvas — the free-placement nest editor (2026-10-01).
 *
 * The Print Preview centre becomes an editable roll strip (Fabric 6):
 *  - tiles are moved anywhere (overlap allowed; the one you grab comes to the
 *    front), corner handles resize with the aspect kept, side handles change
 *    one side and the image is centre-cropped to the box (never stretched) —
 *    the same crop python/nest_composer.py makes with `fill`, so print = preview;
 *  - a tile hanging off the printable strip (left, right, top) is outlined
 *    red and still prints, cut off; on release the parent shows a toast;
 *  - while resizing, a W" × H" pill follows the pointer;
 *  - the whole strip is always visible: zoom = fit the stage, frozen while the
 *    mouse is down and re-fitted on release (the strip grows as tiles go down);
 *  - thumbnails are dropped in with HTML5 drag (`application/x-bv-nest`).
 *
 * Scene units are INCHES (origin = printable strip top-left); the viewport
 * zoom is px per inch. The parent owns every box; this component only reports
 * changes (onChange on release, never per mouse move).
 *
 * Badges, size pills, red outlines and snap guides are drawn on contextTop in
 * screen space, so they never reach an export (the composer renders print
 * from the boxes, not from this canvas).
 */
import { useEffect, useRef, useState } from 'react';
import { Canvas, FabricImage, Point, Rect } from 'fabric';
import { ZoomBar, clampZoom } from '../ZoomBar.jsx';
import { coverCrop, isOffPage, NEST_MIN_ITEM_IN } from './nestLayout.js';
import { initNestSnapping } from './nestSnapping.js';
import i18n from '../../../i18n/index.js';

export const NEST_DRAG_TYPE = 'application/x-bv-nest';

const M = { left: 56, right: 88, top: 48, bottom: 44 };    // stage px around the roll band (guides live here; the side room shows parts hanging off the page)
const SIDE = new Set(['ml', 'mr', 'mt', 'mb']);
const TILE_STYLE = {
  originX: 'left', originY: 'top',
  cornerColor: '#FFFFFF', cornerStyle: 'circle', cornerStrokeColor: '#3b82f6', cornerSize: 12,
  borderColor: '#3b82f6', borderScaleFactor: 1.5, borderOpacityWhenMoving: 1, transparentCorners: false,
  lockScalingFlip: true, lockRotation: true, hoverCursor: 'move', objectCaching: false, strokeWidth: 0,
};
const BG = { selectable: false, evented: false, excludeFromExport: true, objectCaching: false, hoverCursor: 'default', strokeWidth: 0 };  // strokeWidth: scene units are inches, the default 1 would be a 1" stroke

// Image source → canvas element, turned 90° counter-clockwise when rotated
// (the composer's PIL rotate(90)). Cached per source.
const elCache = new WeakMap();
function tileElement(src, rotated) {
  if (!src) return null;
  let e = elCache.get(src);
  if (!e) { e = {}; elCache.set(src, e); }
  const k = rotated ? 'r' : 'n';
  if (e[k]) return e[k];
  const sw = src.naturalWidth || src.width, sh = src.naturalHeight || src.height;
  if (!(sw > 0 && sh > 0)) return null;
  const c = document.createElement('canvas');
  c.width = rotated ? sh : sw;
  c.height = rotated ? sw : sh;
  const ctx = c.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  if (rotated) { ctx.translate(0, sw); ctx.rotate(-Math.PI / 2); }
  ctx.drawImage(src, 0, 0, sw, sh);
  e[k] = c;
  return c;
}

// Put a w×h-inch box at (x, y): crop the element to the box aspect (centred), scale uniformly.
function applyBox(img, x, y, w, h) {
  const el = img.getElement();
  const c = coverCrop(el.width, el.height, w, h);
  img.set({ cropX: c.cropX, cropY: c.cropY, width: c.cropW, height: c.cropH, scaleX: w / c.cropW, scaleY: h / c.cropH, left: x, top: y });
  img.setCoords();
}
const boxOf = (o) => ({ x: o.left, y: o.top, w: o.width * o.scaleX, h: o.height * o.scaleY });
const r2 = (v) => Number(v).toFixed(2);   // same rounding as the panel W × H boxes (InchInput)

export default function NestCanvas({
  tiles,            // [{ id, num, x, y, w, h, z, rotated, src }]  (inches; src = ImageBitmap | img | canvas)
  rollWidthIn,
  stripWidthIn,
  activeId,
  busy,
  onSelect,         // (id | null)
  onChange,         // (id, { x?, y?, w?, h?, z? })
  onDropItem,       // (id, xIn, yIn)   tile centre at the drop point
  onRemove,         // (id)
  onOffPageRelease, // (id)
}) {
  const stageRef = useRef(null);
  const canvasElRef = useRef(null);
  const pillRef = useRef(null);
  const fc = useRef(null);                 // Fabric canvas
  const objs = useRef(new Map());          // id -> FabricImage
  const bg = useRef({});                   // { band, paper }
  const live = useRef({});                 // latest props for Fabric handlers
  const ui = useRef({ down: false, grabbed: null, hover: null, moved: null, last: null, size: { w: 0, h: 0 } });
  const [guides, setGuides] = useState(null);   // DOM guide geometry (px)
  const [zoomRel, setZoomRel] = useState(1);     // viewport zoom relative to "fit" (ZoomBar)
  const [dragOver, setDragOver] = useState(false);
  live.current = { tiles, rollWidthIn, stripWidthIn, activeId, busy, onSelect, onChange, onDropItem, onRemove, onOffPageRelease };

  // Roll / strip geometry is read from live.current, never from this render's
  // closure: fit() and layoutBackground() also run from Fabric handlers and the
  // ResizeObserver registered once at mount, which would otherwise keep the
  // FIRST roll's width (bug 2026-10-01: Z6 Roll 1 36" → Roll 2 42" kept 36").
  const geom = () => {
    const { rollWidthIn: roll, stripWidthIn: strip } = live.current;
    return { roll, strip, inset: Math.max(0, (roll - strip) / 2) };
  };
  const stripHOf = () => {
    let h = 0;
    for (const o of objs.current.values()) h = Math.max(h, o.top + o.height * o.scaleY);
    return h;
  };

  // Fit the whole strip (+ headroom to drag into) inside the stage, no scrolling.
  const fit = () => {
    const c = fc.current, st = ui.current.size;
    const { roll, inset } = geom();
    if (!c || !(st.w > 0 && st.h > 0) || !(roll > 0)) return;
    const stripH = stripHOf();
    const emptyH = Math.min(12, roll / 3);
    const shownH = Math.max(stripH > 0 ? stripH + Math.max(2, stripH * 0.15) : emptyH, 1);
    const availW = Math.max(50, st.w - M.left - M.right), availH = Math.max(50, st.h - M.top - M.bottom);
    const zoom = Math.min(availW / roll, availH / shownH);
    ui.current.fitZoom = zoom;
    if (!ui.current.down && !ui.current.userZoom) {
      const offX = M.left + (availW - roll * zoom) / 2 + inset * zoom;
      c.setViewportTransform([zoom, 0, 0, zoom, offX, M.top]);
      setZoomRel(1);
    }
    layoutBackground();
  };
  // ZoomBar / Ctrl+wheel: zoom about a stage point (default: the centre); 1 = back to fit (and fit() takes over again).
  const zoomTo = (rel, at) => {
    const c = fc.current, st = ui.current.size; if (!c || !ui.current.fitZoom) return;
    if (Math.abs(rel - 1) < 1e-6) { ui.current.userZoom = false; fit(); return; }
    ui.current.userZoom = true;
    c.zoomToPoint(at || new Point(st.w / 2, st.h / 2), ui.current.fitZoom * rel);
    setZoomRel(rel);
    layoutBackground();
  };

  // Roll band + white paper (the paper is the snap target "clip").
  const layoutBackground = () => {
    const c = fc.current; if (!c) return;
    const { band, paper } = bg.current;
    const { roll, strip, inset } = geom();
    const v = c.viewportTransform, zoom = v[0];
    const stripH = stripHOf();
    const paperH = stripH > 0 ? stripH : Math.min(12, roll / 3);
    const bottomIn = (ui.current.size.h - v[5]) / zoom;
    band.set({ left: -inset, top: 0, width: roll, height: Math.max(paperH, bottomIn) });
    paper.set({ left: 0, top: 0, width: strip, height: paperH });
    band.setCoords(); paper.setCoords();
    setGuides({
      bandLeft: v[4] - inset * zoom, bandW: roll * zoom,
      paperLeft: v[4], paperW: strip * zoom, top: v[5], paperH: paperH * zoom,
      rollIn: roll, stripHIn: stripH, empty: objs.current.size === 0,
    });
    c.requestRenderAll();
  };

  // ── create / dispose the Fabric canvas ──────────────────────────────────
  useEffect(() => {
    const c = new Canvas(canvasElRef.current, { preserveObjectStacking: true, selection: false, fireRightClick: false, stopContextMenu: true });
    fc.current = c;
    stageRef.current.__nestCanvas = c;   // for the CDP E2E (print_lab/nesting/e2e_cdp_nesting.mjs) to map inches → screen px
    const band = new Rect({ ...BG, fill: '#111827' });
    const paper = new Rect({ ...BG, fill: '#f8fafc', name: 'clip' });
    c.add(band, paper);
    bg.current = { band, paper };
    const unsnap = initNestSnapping(c, { isTarget: (o) => o.name === 'clip' || o.nestId != null });

    c.on('before:render', (e) => { if (!e?.ctx || e.ctx === c.contextContainer) c.contextTop && c.clearContext(c.contextTop); });
    c.on('after:render', (e) => { if (!e?.ctx || e.ctx === c.contextContainer || e.ctx === c.contextTop) drawOverlay(c); });

    c.on('mouse:down', (e) => {
      ui.current.down = true;
      const o = e.target;
      ui.current.grabbed = o?.nestId ?? null;
      if (!o || o.nestId == null) {
        if (live.current.activeId != null) live.current.onSelect(null);
        if (ui.current.userZoom && e.e) { ui.current.pan = { x: e.e.clientX, y: e.e.clientY }; c.setCursor('grabbing'); }
        return;
      }
      {
        ui.current.last = { ...boxOf(o), width: o.width, height: o.height, cropX: o.cropX, cropY: o.cropY, scaleX: o.scaleX, scaleY: o.scaleY };
        const top = Math.max(0, ...live.current.tiles.map((x) => x.z || 0));
        const mine = live.current.tiles.find((x) => x.id === o.nestId);
        if (mine && (mine.z || 0) < top) { c.bringObjectToFront(o); live.current.onChange(o.nestId, { z: top + 1 }); }
        if (live.current.activeId !== o.nestId) live.current.onSelect(o.nestId);
      }
    });
    c.on('mouse:move', (e) => {
      const p = ui.current.pan; if (!p || !e.e) return;
      c.relativePan(new Point(e.e.clientX - p.x, e.e.clientY - p.y));
      ui.current.pan = { x: e.e.clientX, y: e.e.clientY };
      layoutBackground();
    });
    c.on('mouse:wheel', (e) => {
      const ev = e.e; if (!ev || !(ev.ctrlKey || ev.metaKey)) return;
      ev.preventDefault(); ev.stopPropagation();
      const rel = clampZoom((c.getZoom() / (ui.current.fitZoom || 1)) * (ev.deltaY < 0 ? 1.1 : 1 / 1.1));
      zoomTo(rel, new Point(ev.offsetX, ev.offsetY));
    });
    c.on('object:moving', () => { ui.current.moved = c.getActiveObject()?.nestId ?? null; layoutBackground(); });
    c.on('object:scaling', (e) => {
      const o = e.target; if (!o) return;
      const corner = e.transform?.corner;
      const w = o.width * o.scaleX, h = o.height * o.scaleY;
      if (w < NEST_MIN_ITEM_IN - 1e-6 || h < NEST_MIN_ITEM_IN - 1e-6) {
        const l = ui.current.last;
        if (l) o.set({ left: l.x, top: l.y, width: l.width, height: l.height, cropX: l.cropX, cropY: l.cropY, scaleX: l.scaleX, scaleY: l.scaleY });
      } else if (SIDE.has(corner)) {
        // one side changed: re-crop the image to the new box, keep it unstretched
        const el = o.getElement();
        const cc = coverCrop(el.width, el.height, w, h);
        o.set({ cropX: cc.cropX, cropY: cc.cropY, width: cc.cropW, height: cc.cropH, scaleX: w / cc.cropW, scaleY: h / cc.cropH });
      }
      o.setCoords();
      const b = boxOf(o);
      ui.current.last = { ...b, width: o.width, height: o.height, cropX: o.cropX, cropY: o.cropY, scaleX: o.scaleX, scaleY: o.scaleY };
      ui.current.moved = o.nestId;
      showPill(e.e, `${r2(b.w)}" × ${r2(b.h)}"`);
      layoutBackground();
    });
    c.on('object:modified', (e) => {
      const o = e.target; if (!o || o.nestId == null) return;
      const b = boxOf(o);
      live.current.onChange(o.nestId, { x: +b.x.toFixed(3), y: +b.y.toFixed(3), w: +b.w.toFixed(3), h: +b.h.toFixed(3) });
    });
    c.on('mouse:up', () => {
      if (ui.current.pan) { ui.current.pan = null; c.setCursor('default'); }
      ui.current.down = false;
      ui.current.grabbed = null;
      hidePill();
      const id = ui.current.moved;
      ui.current.moved = null;
      if (id != null) {
        const o = objs.current.get(id);
        if (o && isOffPage(boxOf(o), live.current.stripWidthIn)) live.current.onOffPageRelease(id);
      }
      fit();
      c.renderAll();   // draw the overlay (red cut-off tint, badges) now, not on the next frame
    });
    c.on('mouse:over', (e) => { if (e.target?.nestId != null) { ui.current.hover = e.target.nestId; c.requestRenderAll(); } });
    c.on('mouse:out', (e) => { if (e.target?.nestId != null && ui.current.hover === e.target.nestId) { ui.current.hover = null; c.requestRenderAll(); } });

    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      ui.current.size = { w: Math.floor(width), h: Math.floor(height) };
      c.setDimensions({ width: ui.current.size.w, height: ui.current.size.h });
      fit();
    });
    ro.observe(stageRef.current);
    return () => {
      ro.disconnect();
      unsnap();
      objs.current.clear();
      fc.current = null;
      c.dispose().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── sync the boxes from props ───────────────────────────────────────────
  useEffect(() => {
    const c = fc.current; if (!c) return;
    const seen = new Set();
    const transforming = ui.current.grabbed != null ? objs.current.get(ui.current.grabbed) : null;
    for (const tl of tiles) {
      seen.add(tl.id);
      const el = tileElement(tl.src, tl.rotated);
      if (!el) continue;
      let o = objs.current.get(tl.id);
      if (!o) {
        o = new FabricImage(el, { ...TILE_STYLE });
        o.setControlsVisibility({ mtr: false });
        o.nestId = tl.id;
        objs.current.set(tl.id, o);
        c.add(o);
      } else if (o.getElement() !== el) {
        o.setElement(el);
      } else if (o === transforming) {
        continue;
      }
      applyBox(o, tl.x, tl.y, tl.w, tl.h);
      o.nestNum = tl.num;
    }
    for (const [id, o] of objs.current) {
      if (!seen.has(id)) { c.remove(o); objs.current.delete(id); }
    }
    // stacking: band, paper, then tiles by z
    const order = [...tiles].filter((x) => objs.current.has(x.id)).sort((a, b) => (a.z || 0) - (b.z || 0));
    c.moveObjectTo(bg.current.band, 0);
    c.moveObjectTo(bg.current.paper, 1);
    order.forEach((x, i) => c.moveObjectTo(objs.current.get(x.id), i + 2));
    for (const o of objs.current.values()) o.set({ selectable: !busy, evented: !busy });
    fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiles, rollWidthIn, stripWidthIn, busy]);

  // ── selection follows the parent (card click, panel) ───────────────────
  useEffect(() => {
    const c = fc.current; if (!c) return;
    const o = activeId != null ? objs.current.get(activeId) : null;
    if (o && c.getActiveObject() !== o) c.setActiveObject(o);
    if (!o && c.getActiveObject()) c.discardActiveObject();
    c.requestRenderAll();
  }, [activeId, tiles]);

  // ── keyboard: arrows nudge 0.1" (Shift 1"), Delete removes ─────────────
  useEffect(() => {
    const onKey = (e) => {
      const L = live.current;
      if (L.activeId == null || L.busy) return;
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea' || document.activeElement?.isContentEditable) return;
      const tl = L.tiles.find((x) => x.id === L.activeId);
      if (!tl) return;
      const step = e.shiftKey ? 1 : 0.1;
      const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (d) {
        e.preventDefault();
        L.onChange(tl.id, { x: +(tl.x + d[0]).toFixed(3), y: +(tl.y + d[1]).toFixed(3) });
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        L.onRemove(tl.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── overlay: red off-page outlines, hover, number badges, size pills ───
  function drawOverlay(c) {
    const ctx = c.contextTop; if (!ctx) return;
    const v = c.viewportTransform;
    const strip = live.current.stripWidthIn;
    const font = 'system-ui, -apple-system, Segoe UI, sans-serif';
    ctx.save();
    for (const o of objs.current.values()) {
      const b = boxOf(o);
      const px = b.x * v[0] + v[4], py = b.y * v[3] + v[5], pw = b.w * v[0], ph = b.h * v[3];
      if (isOffPage(b, strip)) {
        // tint what will be cut off (left of, right of or above the paper) and outline the tile
        const pl = v[4], pr = strip * v[0] + v[4], pt = v[5];
        ctx.fillStyle = 'rgba(239,68,68,0.38)';
        if (px < pl) ctx.fillRect(px, py, Math.min(pw, pl - px), ph);
        if (px + pw > pr) ctx.fillRect(Math.max(px, pr), py, px + pw - Math.max(px, pr), ph);
        if (py < pt) ctx.fillRect(Math.max(px, pl), py, Math.min(px + pw, pr) - Math.max(px, pl), Math.min(ph, pt - py));
        ctx.strokeStyle = '#ef4444'; ctx.lineWidth = 3; ctx.setLineDash([]);
        ctx.strokeRect(px + 1.5, py + 1.5, pw - 3, ph - 3);
      } else if (o.nestId === ui.current.hover && o.nestId !== live.current.activeId) {
        ctx.strokeStyle = 'rgba(147,197,253,0.9)'; ctx.lineWidth = 2;
        ctx.strokeRect(px + 1, py + 1, pw - 2, ph - 2);
      }
      // labels go on the part of the tile that is on screen
      const cw = c.getWidth(), chh = c.getHeight();
      const vx0 = Math.max(px, 0), vy0 = Math.max(py, 0), vx1 = Math.min(px + pw, cw), vy1 = Math.min(py + ph, chh);
      const minSide = Math.min(vx1 - vx0, vy1 - vy0);
      if (minSide < 22) continue;
      const r = Math.max(9, Math.min(13, minSide * 0.12));
      const cx = vx0 + r + 6, cy = vy0 + r + 6;
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = o.nestId === live.current.activeId ? '#3b82f6' : 'rgba(15,23,42,0.82)'; ctx.fill();
      ctx.lineWidth = 1.5; ctx.strokeStyle = 'rgba(255,255,255,0.85)'; ctx.stroke();
      ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = `700 ${Math.round(r * 1.1)}px ${font}`;
      ctx.fillText(String(o.nestNum ?? ''), cx, cy + 0.5);
      const label = `${r2(b.w)}" × ${r2(b.h)}"`;
      const fs = Math.max(10, Math.min(13, minSide * 0.1));
      ctx.font = `600 ${Math.round(fs)}px ${font}`;
      const tw = ctx.measureText(label).width;
      if (tw + 16 <= vx1 - vx0 && vy1 - vy0 >= fs * 2 + r * 2 + 16) {
        const bh = fs + 8, bx = vx0 + 6, by = vy1 - bh - 6;
        ctx.fillStyle = 'rgba(15,23,42,0.78)';
        ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(bx, by, tw + 12, bh, 5); else ctx.rect(bx, by, tw + 12, bh); ctx.fill();
        ctx.fillStyle = '#f1f5f9'; ctx.textAlign = 'left';
        ctx.fillText(label, bx + 6, by + bh / 2 + 0.5);
      }
    }
    ctx.restore();
  }

  function showPill(ev, text) {
    const el = pillRef.current, st = stageRef.current;
    if (!el || !st || !ev) return;
    const r = st.getBoundingClientRect();
    el.textContent = text;
    el.style.left = `${ev.clientX - r.left}px`;
    el.style.top = `${ev.clientY - r.top - 36}px`;
    el.style.display = 'block';
  }
  function hidePill() { if (pillRef.current) pillRef.current.style.display = 'none'; }

  // ── HTML5 drop from the thumbnails ─────────────────────────────────────
  const onDragOver = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes(NEST_DRAG_TYPE)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!dragOver) setDragOver(true);
  };
  const onDrop = (e) => {
    setDragOver(false);
    const raw = e.dataTransfer?.getData(NEST_DRAG_TYPE);
    if (raw === '' || raw == null) return;
    e.preventDefault();
    const c = fc.current; if (!c) return;
    const r = canvasElRef.current.getBoundingClientRect();
    const v = c.viewportTransform;
    const x = (e.clientX - r.left - v[4]) / v[0], y = (e.clientY - r.top - v[5]) / v[3];
    live.current.onDropItem(raw, x, y);
  };

  const g = guides;
  return (
    <div
      className={`bv-nest-stage${dragOver ? ' is-dragover' : ''}`}
      ref={stageRef}
      onDragOver={onDragOver}
      onDragLeave={(e) => { if (!stageRef.current?.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={onDrop}
    >
      <canvas ref={canvasElRef} />
      {g && (
        <>
          <div className="bv-nest-feeder" style={{ left: g.bandLeft, width: g.bandW, top: g.top - 26 }}>
            <span className="bv-nest-feeder__label">Printer</span>
            <div className="bv-nest-feeder__bar" />
          </div>
          <div className="bv-nest-guide bv-nest-guide--w" style={{ left: g.bandLeft, width: g.bandW, top: g.top + g.paperH + 10 }}>
            <div className="bv-nest-guide__line" style={{ width: '100%' }} />
            <span>{`${g.rollIn}"`}</span>
          </div>
          {!g.empty && (
            <div className="bv-nest-guide bv-nest-guide--h" style={{ left: g.bandLeft + g.bandW + 12, top: g.top, height: g.paperH }}>
              <div className="bv-nest-guide__line" style={{ height: '100%' }} />
              <span className="bv-nest-guide__vtext">{`${g.stripHIn.toFixed(1)}"`}</span>
            </div>
          )}
          {g.empty && (
            <div className="bv-nest-empty" style={{ left: g.paperLeft, width: g.paperW, top: g.top, height: g.paperH }}>
              <strong>{i18n.t('bv:print.nest.dropHere')}</strong>
              <span>{i18n.t('bv:print.nest.dropHint')}</span>
            </div>
          )}
        </>
      )}
      <div className="bv-nest-pill" ref={pillRef} style={{ display: 'none' }} />
      <ZoomBar dark zoom={zoomRel} onZoom={(z) => zoomTo(clampZoom(z))} />
    </div>
  );
}
